import {Component, inject, OnInit} from '@angular/core';
import {Section} from "../../../@domain/section";
import {ApiEnum} from "../../../@application/enum/api.enum";
import {ApiBaseService} from "../../../../../projects/vendors/src/lib/services/api-base.service";
import {MatDialog} from "@angular/material/dialog";
import {PosCartDialogComponent} from "./pos-cart-dialog/pos-cart-dialog.component";
import {CanComponentDeactivate} from "../../../../../projects/vendors/src/lib/guard/can-deactivate.guard";
import {BaseComponent} from "../../../@application/component/base.component";
import {getMenuState, getPlaceOrderState} from "../../../app.reducer";
import {Store} from "@ngrx/store";
import * as fromApp from "../../../app.reducer";
import {CartRequestDetails} from "../../../@domain/cart-request-details";
import {CLEAR_CART} from "../store/place-order/place-order.action";
import {FoodItemQuantity} from "../../../@domain/food-item-quantity";
import {HttpParams} from "@angular/common/http";
import {ActivePos} from "../../../@domain/active-pos";
import {ItemCategory} from "../../../@domain/item-category";
import {FoodItem} from "../../../@domain/food-item";
import {forkJoin} from "rxjs";
import {SET_MENU_RELATED_DATA} from "../store/menu/menu.action";
import {NgxSpinnerService} from "ngx-spinner";
import {ResponsiveService} from "../../../@application/services/responsive.service";
import {KeyboardShortcutsDialogComponent} from "../../../../../projects/vendors/src/lib/components/keyboard-shortcuts-dialog/keyboard-shortcuts-dialog.component";

@Component({
    selector: 'app-point-of-sales',
    templateUrl: './point-of-sales.component.html',
    styleUrl: './point-of-sales.component.scss',
    standalone: false
})
export class PointOfSalesComponent extends BaseComponent implements OnInit, CanComponentDeactivate {
  apiBaseService = inject(ApiBaseService)
  dialog = inject(MatDialog)
  sectionId !: string
  foodCount = 0
  cartFoods: CartRequestDetails[] = [];
  foods: FoodItem[] = [];
  private ngxLoader = inject(NgxSpinnerService)
  private responsiveService = inject(ResponsiveService)

  private store: Store<fromApp.AppState> = inject(Store);

  // Track if we're in tablet/mobile view
  isTabletView = false;

  // Desktop cart visibility toggle (only used on desktop)
  desktopCartVisible = true;

  ngOnInit(): void {
    // Clear cart from previous component (e.g., Place Order) to ensure clean state
    this.store.dispatch(CLEAR_CART());

    // Subscribe to breakpoint changes
    this.subscription$.add(
      this.responsiveService.isTablet$
        .subscribe(matches => {
          this.isTabletView = matches;
          // Reset desktop cart to visible when switching to desktop
          if (!this.isTabletView) {
            this.desktopCartVisible = true;
          }
        })
    )

    this.subscription$.add(
      this.store.select(getPlaceOrderState)
        .subscribe({
          next: value => {
            this.cartFoods = value.cartFoods
            this.foodCount = value.cartFoods.reduce((sum, item) => sum + item.quantity, 0);
          }
        })
    )
    this.subscription$.add(
      this.store.select(getMenuState)
        .subscribe({
          next: value => {
            this.foods = value.menuRelatedData.foodItem
          }
        })
    )
    this.loadAllData()
  }

  canDeactivate(): boolean {
    if (this.cartFoods.length > 0) {
      return window.confirm('You have unsaved changes. Do you really want to leave?')
    }
    return true
  }

  toggleOrderCart() {
    if (this.isTabletView) {
      // On tablet/mobile, ALWAYS open dialog
      this.openCartDialog();
    } else {
      // On desktop, toggle cart visibility
      this.desktopCartVisible = !this.desktopCartVisible;
    }
  }

  getCartTooltip(): string {
    if (this.isTabletView) {
      return 'View Cart';
    }
    return this.desktopCartVisible ? 'Hide Cart' : 'Show Cart';
  }

  openCartDialog() {
    const dialogRef = this.dialog.open(PosCartDialogComponent, {
      width: '90%',
      height: '90vh',
      panelClass: 'cart-dialog',
      data: {sectionId: this.sectionId}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Order created') {
        this.store.dispatch(CLEAR_CART())
      }
    });
  }

  onOrderCart() {
    this.openCartDialog();
  }

  private loadAllData(isLoaderOn: boolean = false): void {
    if (isLoaderOn || this.foods.length === 0) {
      this.ngxLoader.show().then();
    }
    const sectionRequest = this.apiBaseService.GET<Section[]>([ApiEnum.RestaurantModule, ApiEnum.Section], false);
    const quantityRequest = this.apiBaseService.GET<FoodItemQuantity[]>([ApiEnum.FoodModule, ApiEnum.FoodItemQuantityTag], false);

    const posFilter = `isAvailableForAdmin eq true`;
    const posParams = new HttpParams().set('$filter', posFilter);
    const activePosRequest = this.apiBaseService.GET<ActivePos[]>([ApiEnum.FoodModule, ApiEnum.MenuGroup, ApiEnum.ActivePos], false, true, posParams);

    const itemCategoryRequest = this.apiBaseService.GET<ItemCategory[]>([ApiEnum.FoodModule, ApiEnum.FoodItemCategory], false);

    const foodParams = new HttpParams().append('$select', 'id,basePrice,marginPercentage,name,kitchenId,foodCategoryName,foodCategoryId');
    const foodItemRequest = this.apiBaseService.GET<FoodItem[]>([ApiEnum.FoodModule, ApiEnum.Food], false, true, foodParams);

    forkJoin({
      sections: sectionRequest,
      quantities: quantityRequest,
      activePos: activePosRequest,
      itemCategories: itemCategoryRequest,
      foodItems: foodItemRequest
    }).subscribe({
      next: ({sections, quantities, activePos, itemCategories, foodItems}) => {
        this.sectionId = sections.find(x => x.isPos)?.id!;
        this.store.dispatch(SET_MENU_RELATED_DATA({
          payload: {
            foodItem: foodItems,
            quantity: quantities,
            section: sections,
            category: itemCategories,
            activePos: activePos
          }
        }))
        this.ngxLoader.hide().then();
      },
      error: err => {
        console.error('Failed to load one or more resources:', err);
      }
    });
  }

  onNewItem(event: boolean) {
    if (event)
      this.loadAllData(true);
  }

  showKeyboardShortcuts() {
    this.dialog.open(KeyboardShortcutsDialogComponent, {
      width: '560px',
      autoFocus: false,
      disableClose: false
    });
  }
}
